// Pure helpers behind the Proficiency table: the shared body-cell styling, how
// a cell is written to the exported spreadsheet, and how a standard column
// sorts.

// The student-name text colour. A work-in-progress badge reads in it too, so
// thin evidence carries no colour code of its own (OKO2-3893).
export const STUDENT_NAME_COLOR = "#4f4f4f";

export const bodyCellStyles = `
  padding: 0 16px;
  height: 62px;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  vertical-align: middle;
`;

// Firm levels sort before work-in-progress ones (D41.5). Ordering by
// confidence instead would rank one lucky correct answer above five real
// failures — the sort exists for triage, and the firm levels are the only
// cells with enough evidence to act on.
//
// MUST stay in lockstep with PROFICIENCY_SORT_RANK in oko-server
// src/apiserver/services/class_report_service.py: sorting on screen and asking
// the report to come back sorted must produce the same order (@s18).
const SORT_RANK = {
  LEVEL_1: 0,
  LEVEL_2: 1,
  LEVEL_3: 2,
  WIP_LOW: 3,
  WIP_MID: 4,
  WIP_HIGH: 5,
};

const STATUS_LABEL = {
  LEVEL_1: "Level 1",
  LEVEL_2: "Level 2",
  LEVEL_3: "Level 3",
};

const MISSING_CSV_CELL = "N/A";

// The screen shows an em dash while the spreadsheet keeps "N/A": a CSV is
// machine-adjacent, where an em dash is worse than "N/A" (D41.8).
export const proficiencyCsvCell = (entry) => {
  if (!entry || !(entry.status in SORT_RANK)) return MISSING_CSV_CELL;
  const label = STATUS_LABEL[entry.status];
  if (label) return label;
  return `WIP (${entry.solo_correct}/${entry.solo_attempts})`;
};

// The destination is a filtered LIST, not one session's report: the window
// spans up to five sessions, so a single session reference could never
// describe it (D41.1, D41.2).
export const sessionHistoryUrl = ({ classroomId, studentId, standardCode }) => {
  const params = new URLSearchParams();
  if (classroomId) params.set("classroom_id", classroomId);
  if (studentId) params.set("student_id", studentId);
  if (standardCode) params.set("standard_code", standardCode);
  params.set("date_range", "all_time");
  // Session Reports opens By Lesson by default; this link is about sessions.
  params.set("tab", "groups");
  return `/session_history?${params.toString()}`;
};

// A missing cell sorts LAST IN BOTH DIRECTIONS (D41.5a): absence is not the
// lowest value, so it does not participate in the ordering at either end.
export const compareByProficiency = (a, b, standardCode, sortOrder) => {
  const rankA = SORT_RANK[a.standards[standardCode]?.status];
  const rankB = SORT_RANK[b.standards[standardCode]?.status];

  if (rankA === undefined && rankB === undefined) return 0;
  if (rankA === undefined) return 1;
  if (rankB === undefined) return -1;

  return sortOrder === "asc" ? rankA - rankB : rankB - rankA;
};
